import type { CatalogModel, CatalogResponse, CatalogSku } from '@nadoutong/contracts';
import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { localizeCaughtError } from '../api/errorMessages.ts';
import { useDisplayRate } from '../api/useBilling.ts';
import { useCatalog } from '../api/useCatalog.ts';
import { PanelError } from '../components/PanelError.tsx';
import { Skeleton } from '../components/Skeleton.tsx';
import { useT } from '../i18n/index.ts';
import { Chip, Input, PageHeader, TBody, Table, Td, Th, THead, Tr, cx } from '../ui/index.ts';
import { SkuPricesCell, needsNeutralPricingBadge } from './pricing/billing.tsx';
import {
  PriceLine,
  UnitPricingCell,
  hasRateDiscount,
  pricingLinesForModel,
} from './pricing/priceLines.tsx';

/**
 * `/pricing` — the model price surface (epic #950 PR-3, local Field
 * Notes rebuild of the console-shared factory page, formerly
 * `createPricing(useT, 'nxtpath')`). Read-only leaf.
 *
 * Epic #898 P6 (#912): when the Nxtpath brand catalog carries the
 * additive `skus[]` market shape, the page renders it grouped by
 * namespace — namespace header (display name + slug), one row per SKU
 * with effective per-dimension prices (base price × rate_multiplier)
 * and a discount marker when the org multiplier undercuts the platform
 * base. Absent/empty `skus` (pre-#912 platform) falls back to the
 * legacy vendor-grouped model × channel matrices; the fallback goes
 * away with #916.
 *
 * Wire behavior preserved: catalog comes from the Nxtpath brand catalog
 * (the local `useCatalog` shell pins `brand: 'nxtpath'`); filters are
 * aria-pressed toggle button groups (vendor + channel legacy, namespace
 * in the market view) plus a search input; rates render as CNY via the
 * configured display rate, a discount shows the struck-through base
 * rate under the effective one in both views.
 *
 * tenant#117: each market row's prices cell dispatches through the
 * self-describing billing document renderer (`./pricing/billing.tsx`);
 * a catalog without the `billing` key — and geometry ① flat token —
 * renders the incumbent card byte-identically.
 */

interface MatrixChannel {
  readonly id: string;
  readonly slug: string;
  readonly displayName: string;
  readonly description: string | null;
  readonly rateMultiplier: number;
  readonly baseRateMultiplier: number;
}

interface VendorMatrix {
  readonly vendor: string;
  readonly displayName: string;
  readonly channels: readonly MatrixChannel[];
  readonly models: readonly CatalogModel[];
}

interface NamespaceGroup {
  readonly namespaceId: string;
  readonly slug: string;
  readonly displayName: string;
  readonly skus: readonly CatalogSku[];
}

const ALL_FILTER_VALUE = 'all';

/**
 * Each pricing section is its own card (DESIGN.md §4: borders carry
 * hierarchy — one frame per level, never two hairlines side by side).
 *
 * Below `md` the `Table` turns every row into a self-contained card, so
 * the section drops its own chrome there for the same reason `Table`
 * drops the wrapper's — otherwise the row cards sit 1px inside an
 * identical `line`/`rounded-lg`/`card` frame.
 */
const SECTION_CARD_CLASS =
  'overflow-hidden rounded-lg border border-line bg-card max-md:rounded-none max-md:border-0 max-md:bg-transparent';

/**
 * The section card above already supplies the frame, so the table drops
 * the border/radius `Table` adds by default (same idiom as the audit log
 * table inside a `Panel`).
 */
const NESTED_TABLE_CLASS = 'rounded-none border-0';

export function Pricing(): JSX.Element {
  const t = useT();
  const catalogQ = useCatalog();
  const displayRateQ = useDisplayRate();
  const skuGroups = useMemo(() => buildNamespaceGroups(catalogQ.data), [catalogQ.data]);
  const matrices = useMemo(() => buildVendorMatrices(catalogQ.data), [catalogQ.data]);
  const channelOptions = useMemo(() => collectChannelOptions(matrices), [matrices]);
  const [vendorFilter, setVendorFilter] = useState<string>(ALL_FILTER_VALUE);
  const [channelFilter, setChannelFilter] = useState<string>(ALL_FILTER_VALUE);
  const [query, setQuery] = useState('');
  const visibleMatrices = useMemo(
    () => filterVendorMatrices(matrices, vendorFilter, channelFilter, query),
    [channelFilter, matrices, query, vendorFilter],
  );
  const cnyPerUsd = displayRateQ.data?.cnyPerUsd ?? null;

  return (
    <section data-testid="pricing-page" className="flex flex-col gap-4">
      <PageHeader title={t('pricing.title')} sub={t('pricing.subtitle')} testId="pricing-header" />

      {catalogQ.isLoading ? (
        <div className="flex flex-col gap-3" data-testid="pricing-loading">
          {[0, 1].map((i) => (
            <Skeleton key={i} className="h-48" />
          ))}
          <span className="sr-only">{t('pricing.loading')}</span>
        </div>
      ) : catalogQ.isError ? (
        <PanelError
          message={localizeCaughtError(catalogQ.error, t, 'pricing.error')}
          retryLabel={t('pricing.retry')}
          onRetry={() => {
            void catalogQ.refetch();
          }}
          testId="pricing-error"
        />
      ) : skuGroups.length > 0 ? (
        <SkuMarket groups={skuGroups} cnyPerUsd={cnyPerUsd} />
      ) : matrices.length === 0 ? (
        <p className="m-0 text-body text-ink-low" data-testid="pricing-empty">
          {t('pricing.empty')}
        </p>
      ) : (
        <>
          <div className="flex flex-col gap-3 rounded-lg border border-line bg-card p-3">
            <div className="flex flex-wrap items-center gap-2">
              <FilterButton
                active={vendorFilter === ALL_FILTER_VALUE}
                onClick={() => setVendorFilter(ALL_FILTER_VALUE)}
              >
                {t('pricing.filter.all_vendors')}
              </FilterButton>
              {matrices.map((matrix) => (
                <FilterButton
                  key={matrix.vendor}
                  active={vendorFilter === matrix.vendor}
                  onClick={() => setVendorFilter(matrix.vendor)}
                >
                  {matrix.displayName}
                </FilterButton>
              ))}
            </div>

            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-wrap items-center gap-2">
                <FilterButton
                  active={channelFilter === ALL_FILTER_VALUE}
                  onClick={() => setChannelFilter(ALL_FILTER_VALUE)}
                >
                  {t('pricing.filter.all_channels')}
                </FilterButton>
                {channelOptions.map((channel) => (
                  <FilterButton
                    key={channel.id}
                    active={channelFilter === channel.id}
                    onClick={() => setChannelFilter(channel.id)}
                  >
                    {channel.displayName}
                    <span className="font-mono font-normal">
                      ×{formatRateMultiplier(channel.rateMultiplier)}
                    </span>
                  </FilterButton>
                ))}
              </div>
              <Input
                value={query}
                onChange={(event) => setQuery(event.currentTarget.value)}
                placeholder={t('pricing.search.placeholder')}
                aria-label={t('pricing.search.placeholder')}
                className="lg:w-72"
              />
            </div>
          </div>

          {visibleMatrices.length === 0 ? (
            <p className="m-0 rounded-lg border border-line bg-card px-4 py-5 text-body text-ink-low">
              {t('pricing.no_results')}
            </p>
          ) : (
            visibleMatrices.map((matrix) => (
              <VendorPricingSection key={matrix.vendor} matrix={matrix} cnyPerUsd={cnyPerUsd} />
            ))
          )}
        </>
      )}
    </section>
  );
}

function FilterButton({
  active,
  onClick,
  children,
}: {
  readonly active: boolean;
  readonly onClick: () => void;
  readonly children: ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cx(
        'inline-flex min-h-8 items-center gap-1.5 rounded border px-3 py-1.5 text-label transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40',
        active
          ? 'border-forest bg-forest text-on-forest'
          : 'border-line bg-card text-ink-mid hover:bg-active/60 hover:text-ink',
      )}
    >
      {children}
    </button>
  );
}

function SkuMarket({
  groups,
  cnyPerUsd,
}: {
  readonly groups: readonly NamespaceGroup[];
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const t = useT();
  const [namespaceFilter, setNamespaceFilter] = useState<string>(ALL_FILTER_VALUE);
  const [query, setQuery] = useState('');
  const visibleGroups = useMemo(
    () => filterNamespaceGroups(groups, namespaceFilter, query),
    [groups, namespaceFilter, query],
  );

  return (
    <div className="flex flex-col gap-4" data-testid="pricing-sku-market">
      <div className="flex flex-col gap-3 rounded-lg border border-line bg-card p-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <FilterButton
            active={namespaceFilter === ALL_FILTER_VALUE}
            onClick={() => setNamespaceFilter(ALL_FILTER_VALUE)}
          >
            {t('pricing.filter.all_namespaces')}
          </FilterButton>
          {groups.map((group) => (
            <FilterButton
              key={group.namespaceId}
              active={namespaceFilter === group.namespaceId}
              onClick={() => setNamespaceFilter(group.namespaceId)}
            >
              {group.displayName}
            </FilterButton>
          ))}
        </div>
        <Input
          value={query}
          onChange={(event) => setQuery(event.currentTarget.value)}
          placeholder={t('pricing.search.placeholder')}
          aria-label={t('pricing.search.placeholder')}
          className="lg:w-72"
        />
      </div>

      {visibleGroups.length === 0 ? (
        <p className="m-0 rounded-lg border border-line bg-card px-4 py-5 text-body text-ink-low">
          {t('pricing.no_results')}
        </p>
      ) : (
        visibleGroups.map((group) => (
          <NamespaceSection key={group.namespaceId} group={group} cnyPerUsd={cnyPerUsd} />
        ))
      )}
    </div>
  );
}

function NamespaceSection({
  group,
  cnyPerUsd,
}: {
  readonly group: NamespaceGroup;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const t = useT();
  return (
    <article data-testid={`pricing-namespace-${group.slug}`} className={SECTION_CARD_CLASS}>
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-line-soft px-4 py-3">
        <div className="flex flex-col gap-0.5">
          <h2 className="m-0 text-heading text-ink">{group.displayName}</h2>
          <p className="m-0 text-caption text-ink-low">
            <span className="font-mono">{group.slug}</span>
            {' · '}
            {t('pricing.namespace.summary', { skus: group.skus.length })}
          </p>
        </div>
        {/* A group with unit-metered rows cannot honestly claim the per-Mtok
            dimension for the whole table — its badge goes neutral (#1706);
            each unit line already names its own dimension (Per image).
            tenant#117 extends the same rule to billing documents beyond
            the flat token card (tiers, unit prices, placeholders). */}
        <span className="rounded border border-line-soft bg-raised px-2 py-0.5 font-mono text-mono-label text-ink-low">
          {t(
            group.skus.some((sku) => needsNeutralPricingBadge(sku))
              ? 'pricing.unit.neutral'
              : 'pricing.unit',
          )}
        </span>
      </header>

      <Table className={NESTED_TABLE_CLASS}>
        <THead>
          <Tr interactive={false}>
            <Th className="w-[280px] min-w-[240px]">{t('pricing.column.model')}</Th>
            <Th className="min-w-[160px]">{t('pricing.column.rate')}</Th>
            <Th className="min-w-[320px] border-l border-line-soft">
              {t('pricing.column.prices')}
            </Th>
          </Tr>
        </THead>
        <TBody>
          {group.skus.map((sku) => (
            <Tr key={sku.sku_id} interactive={false} data-testid={`pricing-sku-${sku.sku_id}`}>
              <Td heading className="align-top">
                <span className="flex max-w-[260px] flex-col gap-1 max-md:max-w-none">
                  <span className="break-all font-mono text-body font-medium text-ink">
                    {sku.sku_name}
                  </span>
                  {sku.model_display_name !== null ? (
                    <span className="text-label text-ink-low">{sku.model_display_name}</span>
                  ) : null}
                </span>
              </Td>
              <Td label={t('pricing.column.rate')} className="align-top">
                <span className="flex flex-wrap items-center gap-1.5">
                  <span className="rounded bg-forest-tint px-1.5 py-0.5 font-mono text-mono-label text-forest">
                    ×{formatRateMultiplier(sku.rate_multiplier)}
                  </span>
                  {hasRateDiscount(sku.rate_multiplier, sku.base_rate_multiplier) ? (
                    <>
                      <span className="font-mono text-mono-label font-normal text-ink-low line-through">
                        ×{formatRateMultiplier(sku.base_rate_multiplier)}
                      </span>
                      <Chip tone="ok" lamp={false} testId={`pricing-sku-discount-${sku.sku_id}`}>
                        {t('pricing.sku.discount')}
                      </Chip>
                    </>
                  ) : null}
                </span>
              </Td>
              <Td
                label={t('pricing.column.prices')}
                stack
                className="border-l border-line-soft align-top max-md:border-l-0"
                data-testid={`pricing-sku-prices-${sku.sku_id}`}
              >
                {/* tenant#117: the billing-document dispatcher. A missing
                    key and geometry ① fall through to the incumbent
                    rendering (#1706 unit rows / flat per-Mtok card). */}
                <SkuPricesCell sku={sku} cnyPerUsd={cnyPerUsd} />
              </Td>
            </Tr>
          ))}
        </TBody>
      </Table>
    </article>
  );
}

function VendorPricingSection({
  matrix,
  cnyPerUsd,
}: {
  readonly matrix: VendorMatrix;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const t = useT();
  return (
    <article data-testid={`pricing-vendor-${matrix.vendor}`} className={SECTION_CARD_CLASS}>
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-line-soft px-4 py-3">
        <div className="flex flex-col gap-0.5">
          <h2 className="m-0 text-heading text-ink">{matrix.displayName}</h2>
          <p className="m-0 text-caption text-ink-low">
            {t('pricing.vendor.summary', {
              models: matrix.models.length,
              channels: matrix.channels.length,
            })}
          </p>
        </div>
        {/* Same neutral-badge rule as the SKU market (#1706). */}
        <span className="rounded border border-line-soft bg-raised px-2 py-0.5 font-mono text-mono-label text-ink-low">
          {t(
            matrix.models.some((model) => model.unit_pricing !== undefined)
              ? 'pricing.unit.neutral'
              : 'pricing.unit',
          )}
        </span>
      </header>

      {matrix.channels.length === 0 ? (
        <p
          className="m-0 px-4 py-5 text-body text-ink-low"
          data-testid={`pricing-vendor-${matrix.vendor}-empty`}
        >
          {t('pricing.vendor.no_channels')}
        </p>
      ) : (
        <Table className={NESTED_TABLE_CLASS}>
          <THead>
            <Tr interactive={false}>
              <Th className="w-[280px] min-w-[240px]">{t('pricing.column.model')}</Th>
              {matrix.channels.map((channel) => (
                <Th key={channel.id} className="min-w-[220px] border-l border-line-soft">
                  <span className="flex flex-col gap-1.5 py-1">
                    <span className="text-body font-medium text-ink">{channel.displayName}</span>
                    <span className="font-mono text-mono-label font-normal text-ink-low">
                      {channel.slug}
                    </span>
                    {channel.description != null ? (
                      <span className="max-w-[220px] whitespace-normal text-caption font-normal leading-4 text-ink-low">
                        {channel.description}
                      </span>
                    ) : null}
                    <span className="flex flex-wrap items-center gap-1.5">
                      <span className="rounded bg-forest-tint px-1.5 py-0.5 font-mono text-mono-label text-forest">
                        ×{formatRateMultiplier(channel.rateMultiplier)}
                      </span>
                      {hasDiscount(channel) ? (
                        <span className="font-mono text-mono-label font-normal text-ink-low line-through">
                          ×{formatRateMultiplier(channel.baseRateMultiplier)}
                        </span>
                      ) : null}
                    </span>
                  </span>
                </Th>
              ))}
            </Tr>
          </THead>
          <TBody>
            {matrix.models.map((model) => (
              <Tr key={model.model} interactive={false}>
                <Td heading className="align-top">
                  <span className="flex max-w-[260px] flex-col gap-1 max-md:max-w-none">
                    <span className="break-all font-mono text-body font-medium text-ink">
                      {model.model}
                    </span>
                    {model.display_name !== null ? (
                      <span className="text-label text-ink-low">{model.display_name}</span>
                    ) : null}
                  </span>
                </Td>
                {matrix.channels.map((channel) => (
                  <Td
                    key={`${model.model}::${channel.id}`}
                    label={channel.displayName}
                    stack
                    className="border-l border-line-soft align-top max-md:border-l-0"
                    data-testid={`pricing-cell-${matrix.vendor}-${model.model}-${channel.slug}`}
                  >
                    <PriceCell model={model} channel={channel} cnyPerUsd={cnyPerUsd} />
                  </Td>
                ))}
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
    </article>
  );
}

function PriceCell({
  model,
  channel,
  cnyPerUsd,
}: {
  readonly model: CatalogModel;
  readonly channel: MatrixChannel;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const t = useT();
  const covered = model.channels.some((c) => c.id === channel.id);
  if (!covered) {
    return (
      <span className="inline-flex min-h-[96px] w-full items-center justify-center rounded border border-dashed border-line text-label text-ink-low">
        {t('pricing.cell.unavailable')}
      </span>
    );
  }
  if (model.unit_pricing !== undefined) {
    // Unit-metered model (#1706): the legacy matrix cell renders per-unit
    // prices with the channel's multiplier chain; the zero-rate token
    // anchor dimensions are deliberately not rendered.
    return (
      <UnitPricingCell
        unitPricing={model.unit_pricing}
        rateMultiplier={channel.rateMultiplier}
        baseRateMultiplier={channel.baseRateMultiplier}
        cnyPerUsd={cnyPerUsd}
      />
    );
  }
  return (
    <dl className="m-0 grid min-w-[200px] grid-cols-2 gap-x-4 gap-y-3">
      {pricingLinesForModel(model).map((line) => (
        <PriceLine
          key={line.labelKey}
          label={t(line.labelKey)}
          value={line.value}
          notBilled={line.notBilled}
          notBilledLabel={t('pricing.rate.not_billed')}
          rateMultiplier={channel.rateMultiplier}
          baseRateMultiplier={channel.baseRateMultiplier}
          cnyPerUsd={cnyPerUsd}
        />
      ))}
    </dl>
  );
}

function compareChannelsByPrice(a: MatrixChannel, b: MatrixChannel): number {
  const priceDelta = a.rateMultiplier - b.rateMultiplier;
  if (priceDelta !== 0) return priceDelta;
  return a.displayName.localeCompare(b.displayName) || a.id.localeCompare(b.id);
}

function hasDiscount(channel: MatrixChannel): boolean {
  return hasRateDiscount(channel.rateMultiplier, channel.baseRateMultiplier);
}

function formatRateMultiplier(value: number): string {
  return value.toFixed(4);
}

function skuMatchesQuery(sku: CatalogSku, query: string): boolean {
  const trimmed = query.trim().toLowerCase();
  if (trimmed.length === 0) return true;
  return [
    sku.sku_name,
    sku.model,
    sku.model_display_name,
    sku.vendor,
    sku.namespace_slug,
    sku.namespace_display_name,
  ]
    .filter((value): value is string => typeof value === 'string')
    .some((value) => value.toLowerCase().includes(trimmed));
}

function filterNamespaceGroups(
  groups: readonly NamespaceGroup[],
  namespaceFilter: string,
  query: string,
): readonly NamespaceGroup[] {
  return groups
    .filter(
      (group) => namespaceFilter === ALL_FILTER_VALUE || group.namespaceId === namespaceFilter,
    )
    .map((group) => ({
      ...group,
      skus: group.skus.filter((sku) => skuMatchesQuery(sku, query)),
    }))
    .filter((group) => group.skus.length > 0);
}

function buildNamespaceGroups(catalog: CatalogResponse | undefined): readonly NamespaceGroup[] {
  const skus = catalog?.skus;
  if (skus === undefined || skus.length === 0) return [];
  const groups = new Map<
    string,
    {
      readonly namespaceId: string;
      readonly slug: string;
      readonly displayName: string;
      readonly skus: CatalogSku[];
    }
  >();
  for (const sku of skus) {
    const existing = groups.get(sku.namespace_id);
    if (existing === undefined) {
      groups.set(sku.namespace_id, {
        namespaceId: sku.namespace_id,
        slug: sku.namespace_slug,
        displayName: sku.namespace_display_name,
        skus: [sku],
      });
    } else {
      existing.skus.push(sku);
    }
  }
  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      skus: [...group.skus].sort((a, b) => a.sku_name.localeCompare(b.sku_name)),
    }))
    .sort((a, b) => a.displayName.localeCompare(b.displayName) || a.slug.localeCompare(b.slug));
}

function modelMatchesQuery(model: CatalogModel, query: string): boolean {
  const trimmed = query.trim().toLowerCase();
  if (trimmed.length === 0) return true;
  return [model.model, model.display_name, model.family, model.vendor]
    .filter((value): value is string => typeof value === 'string')
    .some((value) => value.toLowerCase().includes(trimmed));
}

function filterVendorMatrices(
  matrices: readonly VendorMatrix[],
  vendorFilter: string,
  channelFilter: string,
  query: string,
): readonly VendorMatrix[] {
  return matrices
    .filter((matrix) => vendorFilter === ALL_FILTER_VALUE || matrix.vendor === vendorFilter)
    .map((matrix) => {
      const channels =
        channelFilter === ALL_FILTER_VALUE
          ? matrix.channels
          : matrix.channels.filter((channel) => channel.id === channelFilter);
      const models = matrix.models.filter((model) => {
        if (!modelMatchesQuery(model, query)) return false;
        if (channelFilter === ALL_FILTER_VALUE) return true;
        return model.channels.some((channel) => channel.id === channelFilter);
      });
      return { ...matrix, channels, models };
    })
    .filter((matrix) => matrix.channels.length > 0 && matrix.models.length > 0);
}

function collectChannelOptions(matrices: readonly VendorMatrix[]): readonly MatrixChannel[] {
  const channels = new Map<string, MatrixChannel>();
  for (const matrix of matrices) {
    for (const channel of matrix.channels) {
      if (!channels.has(channel.id)) channels.set(channel.id, channel);
    }
  }
  return Array.from(channels.values()).sort(compareChannelsByPrice);
}

function buildVendorMatrices(catalog: CatalogResponse | undefined): readonly VendorMatrix[] {
  if (catalog === undefined) return [];
  const vendorNames = new Map(catalog.vendors.map((v) => [v.slug, v.display_name]));
  const vendors = new Set<string>();
  for (const vendor of catalog.vendors) vendors.add(vendor.slug);
  for (const model of catalog.models) vendors.add(model.vendor);

  return Array.from(vendors)
    .sort((a, b) => a.localeCompare(b))
    .map((vendor) => {
      const models = catalog.models
        .filter((m) => m.vendor === vendor)
        .sort((a, b) => a.model.localeCompare(b.model));
      const channels = new Map<string, MatrixChannel>();
      for (const model of models) {
        for (const channel of model.channels) {
          if (!channels.has(channel.id)) {
            channels.set(channel.id, {
              id: channel.id,
              slug: channel.slug,
              displayName: channel.display_name,
              description: channel.description ?? null,
              rateMultiplier: channel.rate_multiplier,
              baseRateMultiplier: channel.base_rate_multiplier ?? channel.rate_multiplier,
            });
          }
        }
      }
      return {
        vendor,
        displayName: vendorNames.get(vendor) ?? vendor,
        channels: Array.from(channels.values()).sort(compareChannelsByPrice),
        models,
      };
    })
    .filter((m) => m.models.length > 0);
}
